"use client";
import { BlocksRenderer, type BlocksContent } from "@strapi/blocks-react-renderer";

interface MyRendererProps {
    content: BlocksContent;
}

export default function EnhancedBlockRenderer({ content }: MyRendererProps) {

    if(!content){
        return <></>
    }

    return (
        <BlocksRenderer
            content={content}
            blocks={{
                // 1. HANDLE HEADINGS (H1 - H6)
                heading: ({ children, level }) => {
                    switch (level) {
                        case 1:
                            return (
                                <h1 className="text-4xl font-extrabold text-gray-900 my-6 tracking-tight">
                                    {children}
                                </h1>
                            );
                        case 2:
                            return (
                                <h2 className="text-3xl font-bold text-gray-800 my-5 tracking-tight border-b pb-2">
                                    {children}
                                </h2>
                            );
                        case 3:
                            return (
                                <h3 className="text-2xl font-semibold text-gray-800 my-4">
                                    {children}
                                </h3>
                            );
                        case 4:
                            return (
                                <h4 className="text-xl font-semibold text-gray-700 my-3">
                                    {children}
                                </h4>
                            );
                        case 5:
                            return (
                                <h5 className="text-lg font-medium text-gray-700 my-2">
                                    {children}
                                </h5>
                            );
                        case 6:
                            return (
                                <h6 className="text-base font-medium text-gray-600 my-2 uppercase tracking-wider">
                                    {children}
                                </h6>
                            );
                        default:
                            return (
                                <h2 className="text-xl font-bold text-gray-800 my-4">
                                    {children}
                                </h2>
                            );
                    }
                },

                // 2. HANDLE THE LIST CONTAINER
                list: ({ children, format }) => {
                    if (format === 'unordered') {
                        return (
                            <ul className="my-6 pl-2 space-y-3 list-none">
                                {children}
                            </ul>
                        );
                    }

                    return (
                        <ol className="my-6 pl-6 space-y-3 list-decimal text-gray-700">
                            {children}
                        </ol>
                    );
                },

                // 3. HANDLE INDIVIDUAL LIST ITEMS
                'list-item': ({ children }) => {
                    return (
                        <li className="text-base text-gray-700 leading-relaxed flex items-start gap-2">
                            <span className="select-none text-blue-600 font-mono text-xs mt-[6px] block group-data-[list-type=ordered]:hidden">
                                ▶
                            </span>
                            <div>{children}</div>
                        </li>
                    );
                },
            }}
        />
    );
}